"use client";

import { ColumnDef } from "@tanstack/react-table";
import { fmt, Tooltip, TooltipContent, TooltipTrigger } from "visuais";
import {
  Fatura,
  ColunaSelectFiltro,
  AcoesFatura,
  HeaderComFiltro,
  HeaderComFiltroData,
  getStatusPreset,
  BadgeWithTooltip,
} from "@FN";
import { ObservacaoBadge } from "./components/observacao";
import { AvatarComponent } from "@/_modulos/usuario";

export const faturasColumns: ColumnDef<Fatura>[] = [
  {
    accessorKey: "Z15_USRINC",
    header: ({ column, table }) => (
      <ColunaSelectFiltro
        column={column}
        table={table}
        label="Usuário"
        accessorKey="Z15_USRINC"
      />
    ),
    cell: ({ row }) => {
      const { Z15_USRINC, Z15_USRAPR } = row.original;
    
      return (
        <div className="flex justify-center items-center w-[60px] min-w-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="relative inline-flex cursor-default" tabIndex={0}>
                <span className="absolute inset-0 rounded-full bg-gradient-to-tr from-cyan-300 via-cyan-100 to-cyan-300 opacity-60 animate-pulse blur-xl"></span>
                <AvatarComponent username={Z15_USRINC} className="size-8" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" align="center" className="text-xs z-50">
              <p><strong>Incluído:</strong> {Z15_USRINC || "-"}</p>
              <p><strong>Aprovado:</strong> {Z15_USRAPR || "-"}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      );
    },
    size: 0,
    minSize: 75,
    maxSize: 75,
  },  
  {
    accessorKey: "Z15_STATUS",
    header: ({ column, table }) => (
      <ColunaSelectFiltro
        column={column}
        table={table}
        label="Status"
        accessorKey="Z15_STATUS"
      />
    ),
    cell: ({ row }) => {
      const status = row.getValue("Z15_STATUS") as string;
      const preset = getStatusPreset(status);
      return (
        <div className="text-start">
          <BadgeWithTooltip preset={preset} text={status} />
        </div>
      );
    },
    size: 120,
    minSize: 100,
    maxSize: 140,
  },
  {
    accessorKey: "Z15_COD",
    header: ({ column }) => (
      <HeaderComFiltro
        column={column}
        label="Número"
        placeholder="Filtrar número..."
      />
    ),
    cell: ({ row }) => {
      const numero = row.getValue("Z15_COD") as string;
      return <span className="text-start">{numero}</span>;
    },
    size: 120,
    minSize: 100,
    maxSize: 140,
  },
  {
    accessorKey: "Z15_FILIAL",
    header: ({ column, table }) => (
      <ColunaSelectFiltro
        column={column}
        table={table}
        label="Filial"
        accessorKey="Z15_FILIAL"
      />
    ),
    cell: ({ row }) => {
      const filial = row.getValue("Z15_FILIAL") as string;
      return <span className="text-start">{filial}</span>;
    },
    size: 100,
    minSize: 80,
    maxSize: 120,
  },
  {
    accessorKey: "nomeFornecedor",
    header: ({ column }) => (
      <HeaderComFiltro
        column={column}
        label="Fornecedor"
        placeholder="Filtrar fornecedor..."
      />
    ),
    cell: ({ row }) => {
      const fornecedor = row.getValue("nomeFornecedor") as string;
      return (
        <span className="text-start truncate block" title={fornecedor}>
          {fornecedor}
        </span>
      );
    },
    size: 200,
    minSize: 180,
    maxSize: 250,
  },
  {
    accessorKey: "Z15_DTINC",
    filterFn: (row, columnId, value: { from?: Date; to?: Date }) => {
      const raw = row.getValue(columnId) as string;
      if (!raw) return false;

      const date = new Date(
        Number(raw.slice(0, 4)),
        Number(raw.slice(4, 6)) - 1,
        Number(raw.slice(6, 8))
      );

      return (
        (!value.from || date >= value.from) && (!value.to || date <= value.to)
      );
    },
    header: ({ column }) => (
      <HeaderComFiltroData
        column={column}
        label="Data Inclusão"
        accessorKey="Z15_DTINC"
      />
    ),
    cell: ({ row }) => {
      const data = row.original as any;
      return (
        <span className="text-sm text-start">
          {fmt.dates.BR(data.Z15_DTINC)}
        </span>
      );
    },
    size: 130,
    minSize: 120,
    maxSize: 150,
  },
  {
    accessorKey: "Z15_VALOR",
    header: ({ column }) => (
      <HeaderComFiltro
        column={column}
        label="Valor"
        placeholder="Filtrar valor..."
      />
    ),
    cell: ({ row }) => {
      const valor = row.getValue("Z15_VALOR") as string;
      return (
        <div className="font-medium text-start">
          {fmt.numbers.currency(valor)}
        </div>
      );
    },
    size: 140,
    minSize: 120,
    maxSize: 160,
  },
  {
    accessorKey: "Z15_OBS",
    header: "Observação",
    cell: ({ row }) => {
      const obs = row.getValue("Z15_OBS") as string;
      return <ObservacaoBadge observacao={obs} />;
    },
    size: 150,
    minSize: 150,
    maxSize: 150,
  },
  {
    id: "acoes",
    header: "Ações",
    cell: ({ row }) => <AcoesFatura fatura={row.original} />,
    size: 100,
    minSize: 80,
    maxSize: 120,
    enableResizing: false,
  },
];
